import React from "react";

type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
  full: "max-w-7xl",
};

/**
 * Coquille commune à toutes les modales : voile, carte centrée, en-tête avec
 * titre + fermeture, corps défilant et pied d'actions optionnel.
 *
 * `bodyClassName` permet d'ajuster le rembourrage du corps (formulaires denses,
 * tableaux pleine largeur…) sans redéfinir la carte.
 */
export const ModalShell = ({
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = "md",
  bodyClassName = "px-5 py-4",
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  bodyClassName?: string;
}) => (
  <>
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        className={`flex max-h-full w-full flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-xl sm:max-h-[92vh] sm:rounded-2xl ${SIZE_CLASSES[size]}`}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-bold tracking-tight text-slate-900">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 font-Nunito text-sm text-slate-500">
                {subtitle}
              </p>
            ) : null}
          </div>

          <ModalClose onClose={onClose} />
        </div>

        <div className={`min-h-0 flex-1 overflow-y-auto ${bodyClassName}`}>
          {children}
        </div>

        {footer ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>

    <button
      type="button"
      aria-label="Fermer"
      tabIndex={-1}
      onClick={onClose}
      className="fixed inset-0 z-40 cursor-default bg-slate-900/40 backdrop-blur-[1px]"
    />
  </>
);

export const ModalClose = ({ onClose }: { onClose: () => void }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label="Fermer"
    title="Fermer"
    className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:outline-none"
  >
    <svg
      aria-hidden="true"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </button>
);

type ModalButtonVariant = "primary" | "secondary" | "danger" | "success";

const BUTTON_CLASSES: Record<ModalButtonVariant, string> = {
  primary:
    "bg-dca-blue text-white shadow-sm hover:bg-indigo-700 focus-visible:ring-indigo-300",
  secondary:
    "border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-300",
  danger:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-300",
  success:
    "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-emerald-300",
};

/**
 * Bouton d'action de modale, aligné visuellement sur `ActionButton`.
 */
export const ModalButton = ({
  children,
  onClick,
  variant = "primary",
  type = "button",
  form,
  disabled = false,
  icon,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: ModalButtonVariant;
  type?: "button" | "submit" | "reset";
  form?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;
}) => (
  <button
    type={type}
    form={form}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl px-3.5 py-2.5 font-Nunito text-sm font-semibold whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_CLASSES[variant]} ${className}`}
  >
    {icon}
    {children}
  </button>
);

/**
 * Message de confirmation centré, utilisé par les modales de suppression.
 */
export const ModalConfirmText = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <p className="py-2 text-center font-Nunito text-sm text-slate-600">
    {children}
  </p>
);

/**
 * Titre de section à l'intérieur du corps d'une modale.
 */
export const ModalSection = ({
  title,
  children,
  className = "",
}: {
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={className}>
    {title ? (
      <h3 className="mb-2 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
        {title}
      </h3>
    ) : null}
    {children}
  </section>
);
